import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { setTokenRefreshing, getAuthChannel, AUTH_EVENTS, broadcastAuthEvent, notifyRefreshStateChange } from '../hooks/useTokenExpiration';

const API_BASE = '/api/v1/customer';

// Request timeout configuration (Task #4212)
const REQUEST_TIMEOUT = 30000; // 30 seconds
const TIMEOUT_ERROR_MESSAGE = 'Request timed out. Please check your connection and try again.';

// Options interface for API methods to support abort signals
export interface RequestOptions {
  signal?: AbortSignal;
}

// Create a dedicated axios instance for customer API calls with credentials
// withCredentials: true enables sending httpOnly cookies automatically
// timeout: 30s prevents requests from hanging indefinitely
const customerAxios = axios.create({
  withCredentials: true, // Critical: Send httpOnly cookies with requests
  timeout: REQUEST_TIMEOUT,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

// ============================================================================
// Token Refresh Race Condition Prevention (httpOnly Cookie Version)
// ============================================================================
// When multiple API calls receive 401 simultaneously, we use a global promise
// to ensure only ONE refresh request is made. All other requests wait for that
// single refresh to complete before retrying.
// Note: Tokens are stored in httpOnly cookies, not localStorage.
// ============================================================================

// Global state for token refresh coordination
let refreshPromise: Promise<void> | null = null;
let consecutiveRefreshFailures = 0;
const MAX_REFRESH_FAILURES = 3;

// ============================================================================
// Exponential Backoff Configuration (Task #4237)
// ============================================================================
// Prevents "thundering herd" during backend outages by enforcing minimum
// intervals between refresh attempts that increase exponentially on failures.
// ============================================================================
const MIN_REFRESH_INTERVAL_MS = 1000;     // 1 second minimum between attempts
const MAX_REFRESH_BACKOFF_MS = 10000;     // 10 second maximum delay cap
const BACKOFF_MULTIPLIER = 2;             // Double delay each consecutive failure
const JITTER_FACTOR = 0.2;                // ±20% jitter to prevent synchronized retries

// Track last refresh attempt time for backoff calculation
let lastRefreshAttemptTime = 0;

// ============================================================================
// Cross-Tab Token Refresh Synchronization (Task #4236)
// ============================================================================
// When multiple tabs attempt token refresh simultaneously, we use BroadcastChannel
// to coordinate so only ONE tab performs the actual refresh. Other tabs wait for
// the refreshing tab to complete and receive the updated token via httpOnly cookies.
// ============================================================================

// Promise that resolves when another tab completes refresh
let crossTabRefreshPromise: Promise<void> | null = null;
let crossTabRefreshResolve: (() => void) | null = null;

// Track if this tab initiated the refresh (vs waiting for another tab)
let isLocalRefresh = false;

// Timeout for waiting on another tab's refresh (Task #4236)
// If another tab doesn't complete refresh within this time, we'll try locally
const CROSS_TAB_REFRESH_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Creates a standardized AbortError (Task #4378).
 * Using a factory ensures consistent error format throughout the module.
 */
const createAbortError = (message = 'Token refresh aborted'): Error => {
  const error = new Error(message);
  error.name = 'AbortError';
  return error;
};

/**
 * Helper to check if an error is an AbortError (Task #4378).
 * AbortErrors should not count as failures or trigger error UI.
 * Exported for use in tests and other modules.
 */
export const isAbortError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.name === 'AbortError' || error.name === 'CanceledError';
  }
  return false;
};

/**
 * Set up cross-tab refresh listener (Task #4236)
 * Listens for REFRESH_STARTED and TOKEN_REFRESHED events from other tabs
 */
const setupCrossTabRefreshListener = (): void => {
  const authChannel = getAuthChannel();
  if (!authChannel) return;

  authChannel.addEventListener('message', (event: MessageEvent) => {
    const { event: authEvent } = event.data || {};

    if (authEvent === AUTH_EVENTS.REFRESH_STARTED) {
      // Another tab started refreshing - create a promise to wait for it
      // Only set up waiting if we're not the one refreshing
      if (!isLocalRefresh && !crossTabRefreshPromise) {
        crossTabRefreshPromise = new Promise((resolve) => {
          crossTabRefreshResolve = resolve;
        });
        // Set up timeout in case the other tab fails silently
        setTimeout(() => {
          if (crossTabRefreshPromise) {
            // Timeout - other tab didn't complete, allow local refresh
            crossTabRefreshResolve?.();
            crossTabRefreshPromise = null;
            crossTabRefreshResolve = null;
          }
        }, CROSS_TAB_REFRESH_TIMEOUT_MS);
      }
    } else if (authEvent === AUTH_EVENTS.TOKEN_REFRESHED) {
      // Another tab completed refresh - resolve our waiting promise
      if (crossTabRefreshResolve) {
        // Reset failure counter since refresh succeeded (in another tab)
        consecutiveRefreshFailures = 0;
        crossTabRefreshResolve();
        crossTabRefreshPromise = null;
        crossTabRefreshResolve = null;
      }
      // Clear any local refresh promise since another tab succeeded
      refreshPromise = null;
    } else if (authEvent === AUTH_EVENTS.REFRESH_CANCELLED) {
      // Task #4378: Another tab cancelled refresh - resolve waiting promise immediately
      // so this tab doesn't wait 10 seconds for nothing
      if (crossTabRefreshResolve) {
        crossTabRefreshResolve();
        crossTabRefreshPromise = null;
        crossTabRefreshResolve = null;
      }
      // Note: Don't reset failure counter - cancellation is not success
    }
  });
};

// Initialize cross-tab listener on module load
setupCrossTabRefreshListener();

/**
 * Calculates the backoff delay based on consecutive failures (Task #4237).
 * Uses exponential backoff: 1s → 2s → 4s → 8s, capped at 10s.
 * Adds jitter (±20%) to prevent synchronized retries across tabs/sessions.
 *
 * @returns The delay in milliseconds to wait before next refresh attempt
 */
const calculateBackoffDelay = (): number => {
  // On first attempt (consecutiveRefreshFailures === 0), still enforce minimum interval
  const failureCount = Math.max(0, consecutiveRefreshFailures);

  // Exponential backoff: MIN * 2^(failures-1), but at least MIN
  // For 0 failures: MIN * 2^(-1) = MIN/2, so use MIN
  // For 1 failure: MIN * 2^0 = MIN (1s)
  // For 2 failures: MIN * 2^1 = 2*MIN (2s)
  // For 3 failures: MIN * 2^2 = 4*MIN (4s)
  const exponentialDelay = failureCount === 0
    ? MIN_REFRESH_INTERVAL_MS
    : MIN_REFRESH_INTERVAL_MS * Math.pow(BACKOFF_MULTIPLIER, failureCount - 1);

  // Cap at maximum to prevent excessive waits
  const cappedDelay = Math.min(exponentialDelay, MAX_REFRESH_BACKOFF_MS);

  // Add jitter: ±20% to prevent synchronized retries across multiple tabs/sessions
  // This helps distribute retry load during backend recovery
  const jitter = cappedDelay * JITTER_FACTOR * (Math.random() * 2 - 1);

  return Math.max(MIN_REFRESH_INTERVAL_MS, Math.round(cappedDelay + jitter));
};

/**
 * Enforces the backoff delay before allowing a refresh attempt (Task #4237).
 * If not enough time has passed since the last attempt, waits for the remaining delay.
 * Updates lastRefreshAttemptTime after waiting.
 * Task #4378: Now supports cancellation via AbortSignal during the wait.
 *
 * @param signal - Optional AbortSignal to cancel the backoff wait
 */
const enforceRefreshBackoff = async (signal?: AbortSignal): Promise<void> => {
  const now = Date.now();
  const timeSinceLastAttempt = now - lastRefreshAttemptTime;
  const requiredDelay = calculateBackoffDelay();

  if (lastRefreshAttemptTime > 0 && timeSinceLastAttempt < requiredDelay) {
    const waitTime = requiredDelay - timeSinceLastAttempt;
    // Debug logging for backoff enforcement (QA Review - Task #4237)
    if (process.env.NODE_ENV === 'development') {
      console.debug(
        `[Token Refresh] Backoff enforced: waiting ${waitTime}ms ` +
        `(failures: ${consecutiveRefreshFailures}, delay: ${requiredDelay}ms)`
      );
    }

    // Task #4378: Support cancellation during backoff wait
    if (signal) {
      await new Promise<void>((resolve, reject) => {
        // Check if already aborted
        if (signal.aborted) {
          reject(createAbortError());
          return;
        }

        const timeoutId = setTimeout(() => {
          signal.removeEventListener('abort', abortHandler);
          resolve();
        }, waitTime);

        const abortHandler = () => {
          clearTimeout(timeoutId);
          reject(createAbortError());
        };

        signal.addEventListener('abort', abortHandler, { once: true });
      });
    } else {
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }

  // Record this attempt time
  lastRefreshAttemptTime = Date.now();
};

// Extended request config to track retry state
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Performs the actual token refresh operation using httpOnly cookies.
 * This function is called only once per refresh cycle due to the global promise lock.
 * The refresh token is automatically sent via httpOnly cookie.
 * New tokens are set as httpOnly cookies by the server.
 * Includes timeout protection to prevent application freeze (Task #4235).
 * Task #4378: Now supports abort signals for cancellation.
 *
 * @param signal - Optional AbortSignal to cancel the request
 */
const performTokenRefresh = async (signal?: AbortSignal): Promise<void> => {
  // Refresh token is sent automatically via httpOnly cookie
  // withCredentials: true ensures cookies are sent
  // timeout: Prevents indefinite hang if server is unresponsive (Task #4235)
  // signal: Allows cancellation of the refresh request (Task #4378)
  await axios.post(`${API_BASE}/refresh`, {}, {
    withCredentials: true,
    timeout: REQUEST_TIMEOUT,
    timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
    signal,
  });

  // Reset failure counter on successful refresh
  // Note: New tokens are already set as httpOnly cookies by server response
  consecutiveRefreshFailures = 0;
};

/**
 * Gets refreshed tokens via httpOnly cookies, coordinating with other concurrent requests.
 * Only one refresh request is made; all others wait for the same promise.
 * Failure counting happens here, before the refresh attempt.
 * Sets global flag to prevent race condition with token expiration monitor.
 * Task #4236: Now coordinates across browser tabs to prevent simultaneous refresh.
 * Task #4237: Enforces exponential backoff to prevent thundering herd during outages.
 * Task #4378: Now supports abort signals for cancellation.
 *
 * @param signal - Optional AbortSignal to cancel waiting for refresh
 */
const getRefreshedToken = async (signal?: AbortSignal): Promise<void> => {
  // Task #4378: Check if already aborted before starting
  if (signal?.aborted) {
    throw createAbortError();
  }

  // Helper to race a promise against the abort signal (Task #4378)
  // Fixed: Properly cleans up event listeners to prevent memory leaks
  const raceWithSignal = async <T>(promise: Promise<T>): Promise<T> => {
    if (!signal) return promise;

    // If already aborted, reject immediately
    if (signal.aborted) {
      throw createAbortError();
    }

    let abortHandler: (() => void) | null = null;

    const abortPromise = new Promise<T>((_, reject) => {
      abortHandler = () => {
        reject(createAbortError());
      };
      signal.addEventListener('abort', abortHandler, { once: true });
    });

    try {
      // Race the original promise against the abort signal
      return await Promise.race([promise, abortPromise]);
    } finally {
      // CRITICAL: Clean up listener to prevent memory leak
      if (abortHandler) {
        signal.removeEventListener('abort', abortHandler);
      }
    }
  };

  // If a refresh is already in progress in this tab, wait for it (don't count as new attempt)
  // Task #4378: Race against signal so caller can cancel waiting
  if (refreshPromise) {
    return raceWithSignal(refreshPromise);
  }

  // Task #4236: Check if another tab is already refreshing
  // If so, wait for it instead of starting a new refresh
  // Task #4378: Race against signal so caller can cancel waiting
  if (crossTabRefreshPromise) {
    return raceWithSignal(crossTabRefreshPromise);
  }

  // Task #4237: Enforce exponential backoff between refresh attempts
  // This prevents "thundering herd" effect during backend outages
  // Task #4378: Pass signal to allow cancellation during backoff wait
  await enforceRefreshBackoff(signal);

  // This is a new refresh attempt - increment failure counter BEFORE trying
  // (will be reset to 0 on success inside performTokenRefresh)
  consecutiveRefreshFailures++;

  // Signal to token expiration monitor that refresh is in progress
  // This prevents race condition where expiration check triggers logout during refresh
  setTokenRefreshing(true);

  // Task #4377: Notify UI listeners that refresh is starting
  notifyRefreshStateChange(true, null);

  // Task #4236: Mark this as a local refresh and broadcast to other tabs
  isLocalRefresh = true;
  broadcastAuthEvent(AUTH_EVENTS.REFRESH_STARTED);

  // Start a new refresh operation
  // Note: TOKEN_REFRESHED is broadcast by customerAuthSlice.refreshCustomerToken.fulfilled
  // which has the actual expires_in from server response. We don't broadcast here to avoid
  // duplicate/inconsistent expiry times (QA Review - Task #4236).
  // Task #4378: Pass signal to performTokenRefresh for actual cancellation
  refreshPromise = performTokenRefresh(signal)
    .then(() => {
      // Task #4377: Notify UI listeners that refresh succeeded
      notifyRefreshStateChange(false, null);
    })
    .catch((error) => {
      // Task #4378: Don't show error UI for abort/cancellation - user intentionally cancelled
      // CRITICAL: Also decrement failure counter - aborts don't count as failures
      // CRITICAL: Broadcast REFRESH_CANCELLED so other tabs don't wait 10 seconds
      if (isAbortError(error)) {
        consecutiveRefreshFailures = Math.max(0, consecutiveRefreshFailures - 1);
        notifyRefreshStateChange(false, null);
        broadcastAuthEvent(AUTH_EVENTS.REFRESH_CANCELLED);
        throw error; // Re-throw for caller handling
      }
      // Task #4377: Notify UI listeners that refresh failed with sanitized error message
      // Don't expose internal error details (DB connection strings, stack traces, etc.)
      const sanitizedErrorMessage = 'Session refresh failed. Please try logging in again.';
      notifyRefreshStateChange(false, sanitizedErrorMessage);
      // Broadcast failure to other tabs (with sanitized message only)
      broadcastAuthEvent(AUTH_EVENTS.REFRESH_FAILED, { error: sanitizedErrorMessage });
      throw error; // Re-throw original error for internal handling
    })
    .finally(() => {
      // Clear the promise and refresh flag once complete (success or failure)
      refreshPromise = null;
      setTokenRefreshing(false);
      isLocalRefresh = false;
    });

  // Task #4378: Race the refresh promise against the signal
  return raceWithSignal(refreshPromise);
};

/**
 * Clears client-side auth state for refresh coordination.
 * Called when refresh fails or max retries exceeded.
 * Note: httpOnly cookies can only be cleared by server response.
 * Note: consecutiveRefreshFailures is NOT reset here - it accumulates across
 * failures and is only reset on successful refresh (Task #4376).
 * Task #4236: Also clears cross-tab refresh state.
 */
const clearAuthState = (): void => {
  refreshPromise = null;
  // Task #4236: Clear cross-tab refresh state
  crossTabRefreshPromise = null;
  crossTabRefreshResolve = null;
  isLocalRefresh = false;
  // Note: We cannot clear httpOnly cookies from JavaScript
  // The server clears them via Set-Cookie with expired dates on logout
  // Note: consecutiveRefreshFailures is only reset on successful refresh
  // in performTokenRefresh() to properly enforce MAX_REFRESH_FAILURES limit
};

/**
 * Resets the refresh failure counter. Should be called on successful login
 * to ensure user starts fresh after re-authentication (Task #4376).
 * Task #4236: Also clears cross-tab refresh state.
 * Task #4237: Also clears backoff timer to allow immediate refresh.
 */
export const resetRefreshFailureCounter = (): void => {
  consecutiveRefreshFailures = 0;
  refreshPromise = null;
  // Task #4236: Clear cross-tab refresh state
  crossTabRefreshPromise = null;
  crossTabRefreshResolve = null;
  isLocalRefresh = false;
  // Task #4237: Reset backoff timer so next attempt doesn't wait
  lastRefreshAttemptTime = 0;
};

// No request interceptor needed for adding tokens!
// httpOnly cookies are automatically sent by the browser with credentials: true

// Add response interceptor for customer token refresh with race condition prevention
customerAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle 401 errors with coordinated token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // Check if we've exceeded max refresh attempts
      if (consecutiveRefreshFailures >= MAX_REFRESH_FAILURES) {
        clearAuthState();
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }

      try {
        // Refresh tokens via httpOnly cookies (coordinates with other concurrent requests)
        // Only one refresh call is made; concurrent 401s share the same promise
        // Task #4378: Pass signal from original request to allow cancellation
        // Note: Cast GenericAbortSignal to AbortSignal (compatible at runtime)
        await getRefreshedToken(originalRequest.signal as AbortSignal | undefined);

        // Retry original request - new token cookie is automatically sent
        return customerAxios(originalRequest);
      } catch (refreshError) {
        // Task #4378: If refresh was cancelled (abort), propagate without clearing state
        // The user/component intentionally cancelled, no need to logout
        if (isAbortError(refreshError)) {
          return Promise.reject(refreshError);
        }
        // Token refresh failed, clear auth state
        clearAuthState();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Request types
export interface RegisterRequest {
  email: string;
  password: string;
  confirm_password: string;
  first_name: string;
  last_name: string;
  accept_terms: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirm_password: string;
}

// Response types
export interface RegisterResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  created_at: string;
}

// LoginResponse matches backend services.LoginResponseCookie
// Note: Tokens are NOT in response body - they are set as httpOnly cookies
// User data is at root level, NOT nested under 'user'
export interface LoginResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  expires_in: number; // Seconds until token expires (for UI display)
  last_login_at?: string;
}

// RefreshResponse matches backend services.RefreshResponseCookie
export interface RefreshResponse {
  expires_in: number; // Seconds until new token expires
}

export interface CustomerProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  phone?: string;
  avatar_url?: string;
  email_verified: boolean;
  status: string;
  login_count: number;
  last_login_at?: string;
  password_changed_at?: string;
  accepted_terms_at?: string;
  created_at: string;
}

// API response wrapper (matches backend utils.APIResponse)
export interface APIResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Validation error response (matches backend utils.ValidationErrorResponse)
export interface ValidationErrorResponse {
  success: boolean;
  message: string;
  errors: Record<string, string>;
}

// Error response (matches backend utils.ErrorResponse)
export interface ErrorResponse {
  success: boolean;
  message: string;
  error: string;
}

// Dashboard response types
export interface CustomerUpload {
  id: number;
  file_id: string;
  original_name: string;
  file_size: number;
  content_type: string;
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'corrupted';
  file_type: string;
  file_category: string;
  case_number: string;
  uploaded_at: string;
  download_count: number;
}

export interface MyUploadsResponse {
  files: CustomerUpload[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface CustomerCase {
  case_number: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  type: string;
  file_count: number;
  created_at: string;
  last_accessed_at: string;
}

export interface MyCasesResponse {
  cases: CustomerCase[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface UploadsQueryParams {
  page?: number;
  page_size?: number;
  status?: string;
}

export interface CasesQueryParams {
  page?: number;
  page_size?: number;
  status?: string;
  priority?: string;
}

export const customerAPI = {
  /**
   * Register a new customer account
   * Note: Uses regular axios (no auth required)
   * @param data - Registration data
   * @param options - Request options including abort signal
   */
  register: async (data: RegisterRequest, options?: RequestOptions) => {
    return axios.post<APIResponse<RegisterResponse>>(`${API_BASE}/register`, data, {
      timeout: REQUEST_TIMEOUT,
      timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
      signal: options?.signal,
    });
  },

  /**
   * Login with email and password
   * Note: Uses axios with credentials to receive httpOnly cookies
   * Tokens are set as httpOnly cookies, NOT returned in response body
   * @param data - Login credentials
   * @param options - Request options including abort signal
   */
  login: async (data: LoginRequest, options?: RequestOptions) => {
    return axios.post<APIResponse<LoginResponse>>(`${API_BASE}/login`, data, {
      withCredentials: true, // Receive httpOnly cookies from server
      timeout: REQUEST_TIMEOUT,
      timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
      signal: options?.signal,
    });
  },

  /**
   * Verify email with token from email link
   * Note: Uses regular axios (no auth required)
   * @param token - Email verification token
   * @param options - Request options including abort signal
   */
  verifyEmail: async (token: string, options?: RequestOptions) => {
    return axios.get<APIResponse<{ message: string }>>(`${API_BASE}/verify-email`, {
      params: { token },
      timeout: REQUEST_TIMEOUT,
      timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
      signal: options?.signal,
    });
  },

  /**
   * Resend verification email
   * Note: Uses regular axios (no auth required)
   * @param email - Email address to resend verification to
   * @param options - Request options including abort signal
   */
  resendVerification: async (email: string, options?: RequestOptions) => {
    return axios.post<APIResponse<{ message: string }>>(`${API_BASE}/resend-verification`, {
      email,
    }, {
      timeout: REQUEST_TIMEOUT,
      timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
      signal: options?.signal,
    });
  },

  /**
   * Request password reset email
   * Note: Uses regular axios (no auth required)
   * @param email - Email address to send password reset to
   * @param options - Request options including abort signal
   */
  forgotPassword: async (email: string, options?: RequestOptions) => {
    return axios.post<APIResponse<{ message: string }>>(`${API_BASE}/forgot-password`, {
      email,
    }, {
      timeout: REQUEST_TIMEOUT,
      timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
      signal: options?.signal,
    });
  },

  /**
   * Reset password with token
   * Note: Uses regular axios (no auth required)
   * @param data - Reset password data with token
   * @param options - Request options including abort signal
   */
  resetPassword: async (data: ResetPasswordRequest, options?: RequestOptions) => {
    return axios.post<APIResponse<{ message: string }>>(`${API_BASE}/reset-password`, data, {
      timeout: REQUEST_TIMEOUT,
      timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
      signal: options?.signal,
    });
  },

  /**
   * Get current customer profile (requires authentication)
   * Uses customerAxios with customer token interceptor
   * @param options - Request options including abort signal
   */
  getProfile: async (options?: RequestOptions) => {
    return customerAxios.get<APIResponse<CustomerProfile>>(`${API_BASE}/profile`, {
      signal: options?.signal,
    });
  },

  /**
   * Update customer profile (requires authentication)
   * Uses customerAxios with customer token interceptor
   * @param data - Profile data to update
   * @param options - Request options including abort signal
   */
  updateProfile: async (data: Partial<CustomerProfile>, options?: RequestOptions) => {
    return customerAxios.put<APIResponse<CustomerProfile>>(`${API_BASE}/profile`, data, {
      signal: options?.signal,
    });
  },

  /**
   * Refresh customer JWT token
   * Note: Uses credentials mode - refresh token is sent via httpOnly cookie
   * New tokens are set as httpOnly cookies by server response
   * @param options - Request options including abort signal
   */
  refreshToken: async (options?: RequestOptions) => {
    return axios.post<APIResponse<RefreshResponse>>(`${API_BASE}/refresh`, {}, {
      withCredentials: true, // Send refresh token cookie, receive new token cookies
      timeout: REQUEST_TIMEOUT,
      timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
      signal: options?.signal,
    });
  },

  /**
   * Logout customer (invalidate tokens)
   * Uses customerAxios with credentials to clear httpOnly cookies
   * Note: Logout is NOT abortable - it must always complete to clear server state
   */
  logout: async () => {
    return customerAxios.post<APIResponse<{ message: string }>>(`${API_BASE}/logout`);
  },

  /**
   * Get customer's uploaded files (requires authentication)
   * Uses customerAxios with customer token interceptor
   * @param params - Query parameters for pagination/filtering
   * @param options - Request options including abort signal
   */
  getMyUploads: async (params?: UploadsQueryParams, options?: RequestOptions) => {
    return customerAxios.get<APIResponse<MyUploadsResponse>>(`${API_BASE}/uploads`, {
      params,
      signal: options?.signal,
    });
  },

  /**
   * Get customer's cases (requires authentication)
   * Uses customerAxios with customer token interceptor
   * @param params - Query parameters for pagination/filtering
   * @param options - Request options including abort signal
   */
  getMyCases: async (params?: CasesQueryParams, options?: RequestOptions) => {
    return customerAxios.get<APIResponse<MyCasesResponse>>(`${API_BASE}/cases`, {
      params,
      signal: options?.signal,
    });
  },
};

export default customerAPI;
